<script setup lang="ts">
import { defineAsyncComponent, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import {
  NConfigProvider,
  NDialogProvider,
  NMessageProvider,
  NNotificationProvider,
  dateZhCN,
  zhCN,
} from 'naive-ui'
import CelebrationNotificationController from '@/components/celebration/CelebrationNotificationController.vue'
import DesktopUpdateController from '@/components/updater/DesktopUpdateController.vue'
import {
  isDesktopRuntime,
  platformListen,
  showMainWindow,
  type PlatformUnlisten,
} from '@/platform/bridge'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useTokenRefresh } from '@/composables/useTokenRefresh'
import { themeOverrides } from '@/styles/naive-theme'
import { logger } from '@/utils/logger'
import {
  ensureAppSystemNotificationPermission,
  sendAppSystemNotification,
} from '@/utils/systemNotification'

interface TaskNavigationPayload {
  customerId?: number
  customerName?: string
}

interface OrderNavigationPayload {
  customerId?: number
  customerWechat?: string
  customerName?: string
  keyword?: string
  status?: string
}

interface AiTaskNotificationPayload {
  count?: number
  taskIds?: number[]
  customerName?: string
}

const AssistantWidget = defineAsyncComponent(
  () => import('@/components/assistant/AssistantWidget.vue'),
)
const router = useRouter()
const notificationStore = useNotificationStore()
useTokenRefresh()
const desktopEventUnlisteners: PlatformUnlisten[] = []
let desktopEventListenersDisposed = false
let sidebarOrderNavigateSeq = 0

/** 跳转到桌面端请求的后台管理路由。 */
async function handleAppNavigate(target: string) {
  if (typeof target !== 'string' || !target.trim()) return

  try {
    await router.push(target)
  } catch (error) {
    logger.warn('navigate', 'app:navigate router.push failed', error)
  }
}

/** 处理微信侧边栏发起的待办任务跳转。 */
async function handleTaskNavigate(payload: TaskNavigationPayload = {}) {
  await showMainWindow()
  const query: Record<string, string> = {}
  if (Number(payload.customerId) > 0) query.customerId = String(payload.customerId)
  if (payload.customerName) query.customerName = payload.customerName
  await router.push({ path: '/tasks', query })
}

/** 处理微信侧边栏发起的订单列表跳转。 */
async function handleOrderNavigate(payload: OrderNavigationPayload = {}) {
  await showMainWindow()
  const query: Record<string, string> = {
    from: 'sidebarOrder',
    sidebarOrderTrigger: `${Date.now()}-${++sidebarOrderNavigateSeq}`,
  }
  if (Number(payload.customerId) > 0) query.customerId = String(payload.customerId)
  if (payload.customerWechat) query.customerWechat = payload.customerWechat
  if (payload.customerName) query.customerName = payload.customerName
  if (payload.keyword) query.keyword = payload.keyword
  if (payload.status) query.status = payload.status
  await router.push({ path: '/orders', query })
}

function normalizeAiTaskNotificationPayload(
  payload: AiTaskNotificationPayload = {},
): { count: number; taskIds: number[]; customerName: string } | null {
  const count = Number(payload.count) || 0
  if (count <= 0) return null

  const taskIds = Array.isArray(payload.taskIds)
    ? payload.taskIds.map((taskId) => Number(taskId)).filter((taskId) => Number.isFinite(taskId))
    : []

  return {
    count,
    taskIds,
    customerName: typeof payload.customerName === 'string' ? payload.customerName : '',
  }
}

/** 处理侧边栏发出的 AI 新增待办通知。 */
async function handleAiTaskNotification(payload: AiTaskNotificationPayload) {
  const normalized = normalizeAiTaskNotificationPayload(payload)
  if (!normalized) return

  notificationStore.addAiTaskNotification(normalized)
  try {
    if (await ensureAppSystemNotificationPermission()) {
      await sendAppSystemNotification({
        title: `AI 发现 ${normalized.count} 个待办任务`,
        body: normalized.customerName
          ? `${normalized.customerName}：AI 识别到 ${normalized.count} 个待办事项`
          : `AI 识别到 ${normalized.count} 个待办事项`,
      })
    }
  } catch (error) {
    logger.warn('notification', '待办创建系统通知发送失败', error)
  }
}

/** 处理侧边栏发出的 AI 待办完结通知1。 */
async function handleAiTaskCompletedNotification(payload: AiTaskNotificationPayload) {
  const normalized = normalizeAiTaskNotificationPayload(payload)
  if (!normalized) return

  notificationStore.addAiTaskCompletedNotification(normalized)
  try {
    if (await ensureAppSystemNotificationPermission()) {
      await sendAppSystemNotification({
        title: `AI 已完结 ${normalized.count} 个待办任务`,
        body: normalized.customerName
          ? `${normalized.customerName}：AI 自动完结 ${normalized.count} 个待办事项`
          : `AI 自动完结 ${normalized.count} 个待办事项`,
      })
    }
  } catch (error) {
    logger.warn('notification', '待办完结系统通知发送失败', error)
  }
}

/** 统一注册桌面端跨窗口事件，浏览器模式会自动降级为空监听。 */
async function registerDesktopEventListeners() {
  const unlisteners = await Promise.all([
    platformListen<string>('app:navigate', (target) => void handleAppNavigate(target)),
    platformListen<TaskNavigationPayload>('navigate:tasks:ai_recommended', (payload) => {
      void handleTaskNavigate(payload).catch((error) => {
        logger.warn('navigate', 'navigate:tasks:ai_recommended failed', error)
      })
    }),
    platformListen<OrderNavigationPayload>('navigate:orders:list', (payload) => {
      void handleOrderNavigate(payload).catch((error) => {
        logger.warn('navigate', 'navigate:orders:list failed', error)
      })
    }),
    platformListen<AiTaskNotificationPayload>('notification:ai-task', (payload) => {
      void handleAiTaskNotification(payload).catch((error) => {
        logger.warn('notification', 'notification:ai-task 处理失败', error)
      })
    }),
    platformListen<AiTaskNotificationPayload>('notification:ai-task-completed', (payload) => {
      void handleAiTaskCompletedNotification(payload).catch((error) => {
        logger.warn('notification', 'notification:ai-task-completed 处理失败', error)
      })
    }),
  ])

  if (desktopEventListenersDisposed) {
    unlisteners.forEach((unlisten) => unlisten())
    return
  }
  desktopEventUnlisteners.push(...unlisteners)
}

onMounted(() => {
  void registerDesktopEventListeners().catch((error) => {
    logger.warn('navigate', '注册桌面端导航事件失败', error)
  })
})

onUnmounted(() => {
  desktopEventListenersDisposed = true
  desktopEventUnlisteners.splice(0).forEach((unlisten) => unlisten())
})
</script>

<template>
  <n-config-provider
    :theme-overrides="themeOverrides"
    :locale="zhCN"
    :date-locale="dateZhCN"
  >
    <n-message-provider>
      <n-dialog-provider>
        <n-notification-provider>
          <router-view />
          <AssistantWidget />
          <CelebrationNotificationController />
          <DesktopUpdateController v-if="isDesktopRuntime" />
        </n-notification-provider>
      </n-dialog-provider>
    </n-message-provider>
  </n-config-provider>
</template>
